/**
 * Como um valor da planilha vira célula da folha impressa.
 *
 * Célula de checklist (caixaria, vidraçaria, pintura...):
 *   número > 0          → a quantidade (com a descrição, quando houver)
 *   "X" / não se aplica → "×" cinza
 *   vazio ou zero       → em branco, para preencher à caneta
 * Qualquer outro texto sai como está: é informação que alguém escreveu e a
 * folha não pode esconder.
 */

const NAO_SE_APLICA = new Set(["x", "nao se aplica", "n/a", "na"]);
const ISO = /^\d{4}-\d{2}-\d{2}$/;

export const VAZIO = Object.freeze({ tipo: "vazio" });
export const NAO_APLICA = Object.freeze({ tipo: "nao_aplica" });

function normalizar(texto) {
  return String(texto)
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
}

function comoNumero(valor) {
  if (typeof valor === "number") return Number.isFinite(valor) ? valor : null;
  if (typeof valor !== "string") return null;
  const texto = valor.trim().replace(",", ".");
  if (!/^-?\d+(\.\d+)?$/.test(texto)) return null;
  return Number(texto);
}

function ehVazio(valor) {
  return valor === null || valor === undefined || String(valor).trim() === "";
}

export function ehNaoSeAplica(valor) {
  return !ehVazio(valor) && NAO_SE_APLICA.has(normalizar(valor));
}

/** Descrição útil, ou `undefined` para vazio e "X". */
export function lerDescricao(valor) {
  if (ehVazio(valor) || ehNaoSeAplica(valor)) return undefined;
  return String(valor).trim();
}

export function lerItem(valor, descricao) {
  if (ehVazio(valor)) return VAZIO;
  if (ehNaoSeAplica(valor)) return NAO_APLICA;
  const numero = comoNumero(valor);
  if (numero !== null) {
    if (numero <= 0) return VAZIO;
    const texto = lerDescricao(descricao);
    return texto ? { tipo: "qtd", qtd: numero, descricao: texto } : { tipo: "qtd", qtd: numero };
  }
  return { tipo: "texto", texto: String(valor).trim() };
}

/** Coluna de preenchimento manual: em branco, salvo se a planilha já tiver o valor. */
export function lerManual(valor) {
  if (ehVazio(valor)) return VAZIO;
  const texto = String(valor).trim();
  if (ISO.test(texto)) return { tipo: "data", iso: texto };
  if (ehNaoSeAplica(texto)) return NAO_APLICA;
  return { tipo: "texto", texto };
}

export function lerData(valor) {
  return typeof valor === "string" && ISO.test(valor) ? { tipo: "data", iso: valor } : VAZIO;
}

export function lerNumero(valor) {
  const numero = comoNumero(valor);
  return numero === null ? VAZIO : { tipo: "numero", valor: numero };
}

export function lerTexto(valor) {
  return ehVazio(valor) ? VAZIO : { tipo: "texto", texto: String(valor).trim() };
}
